import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Divider from "@mui/material/Divider";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import Fade from "@mui/material/Fade";
import { InlineIcon } from "@iconify/react";
import Tooltip from "@mui/material/Tooltip";
import { useTheme } from "@mui/material/styles";
import { codeStyle } from "./TextPreview";
import { ButtonLink } from "./Image";
import { Instance } from "@popperjs/core";

import { useAtomValue, useSetAtom } from "jotai";

import * as slackemojis from "./slackemojis.json";

import { useState, useEffect, Fragment, useRef } from "react";
import { StoryPopup } from "./StoryCard";
import { DarkStoryPreview } from "./StoryDetails";
import { TextPreview } from "./TextPreview";

import { activeLabelsState, searchState } from "./JotaiAtoms";

import {
  projectPickerState,
  peopleIndexState,
  AccountInfo,
} from "./JotaiAtoms";

import * as u from "./utility";
import * as riki from "jsriki";

import { useNewTabText } from "./hooks";
import { MessageItem, MdPiece } from "./MessageItem";
import { DEFAULT_FONT_SIZE, HIGHTLIGHT_COLOR } from "./constants";
import JSONPretty from "react-json-pretty";
import { FileUploadPreview, UnidiffRust } from "./FileUploadPreview";
import { SlackMention } from "./SlackMention";

const translateEmoji = (name: string) => {
  if (name.length < 3) {
    return null;
  }

  if (name[0] === ":") {
    name = name.slice(1, name.length - 1);
  }

  return (slackemojis as any)[name] || null;
};

function collectTextWithBreaks(node: HTMLElement): string {
  let buffer = "";

  node.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      buffer += child.textContent || "";
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const tag = (child as HTMLElement).tagName.toLowerCase();
      console.log(tag);

      if (tag === "br") {
        buffer += "\n";
      } else {
        buffer += collectTextWithBreaks(child as HTMLElement);

        // Add line break after block-level tags
        if (
          [
            "p",
            "div",
            "section",
            "article",
            "header",
            "footer",
            "h1",
            "h2",
            "h3",
            "h4",
            "h5",
            "h6",
          ].includes(tag)
        ) {
          buffer += "\n";
        }
      }
    }
  });

  return buffer;
}

type CodeLineProps = {
  fontSize?: number;
  st_pk?: string;
  st_sk?: string;
  children: any;
  sx?: any;
};
function CodeLine({ fontSize, st_pk, st_sk, sx, children }: CodeLineProps) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const popperRef = useRef<Instance>(null);

  const positionRef = useRef<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });

  const handleCopy = () => {
    let text = "";
    if (ref.current) {
      text = collectTextWithBreaks(ref.current);
    }

    navigator.clipboard.writeText(text);
    setOpen(false);
  };

  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    let rect = ref.current!.getBoundingClientRect();
    const clientRects = ref.current!.getClientRects();
    if (clientRects.length > 1) {
      rect = clientRects[clientRects.length - 1];
    }
    positionRef.current = { x: rect.x + rect.width, y: rect.y };
    if (popperRef.current != null) {
      popperRef.current.update();
    }
  };

  return (
    <Tooltip
      placement="bottom-end"
      leaveDelay={400}
      enterDelay={0}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      title={
        <Fragment>
          <InlineIcon
            icon="solar:copy-bold"
            style={{
              cursor: "pointer",
              fontSize: "18px",
              color: "var(--accent-primary)",
              opacity: theme.palette.mode === "dark" ? 0.7 : 0.45,
            }}
            onClick={handleCopy}
          />
        </Fragment>
      }
      componentsProps={{
        tooltip: {
          sx: { p: 0, backgroundColor: "transparent" },
        },
      }}
      slotProps={{
        popper: {
          popperRef,
          anchorEl: {
            getBoundingClientRect: () => {
              return new DOMRect(positionRef.current.x, positionRef.current.y);
            },
          },
        },
      }}
      PopperProps={{
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [18, -13],
            },
          },
        ],
      }}
    >
      <Typography
        component="span"
        fontSize={fontSize}
        st_pk={st_pk}
        st_sk={st_sk}
        ref={ref}
        onMouseEnter={handleMouseEnter}
        sx={{
          background: "var(--md-code-bg)",
          color: "var(--md-code-text)",
          px: 0.6,
          py: 0.3,
          borderRadius: "6px",
          fontFamily: "monospace",
          ...(sx ? sx : {}),
        }}
      >
        <>{children}</>
      </Typography>
    </Tooltip>
  );
}

function CodeBlockText({ text }: { text: string }) {
  const [displayText, setDisplayText, handleDoubleClick] = useNewTabText();
  useEffect(() => {
    setDisplayText(text);
  }, [text]);

  return <Box onClick={handleDoubleClick}>{displayText}</Box>;
}

type CodeBlockProps = {
  st_pk: string;
  st_sk: string;
  data: string;
  fontSize: number;
  index: number;
  item: MessageItem;
};

const render = (data: string) => {
  switch (u.determineTextMimeType(data)) {
    case "application/json":
      return <JSONPretty data={data} />;
    case "text/x-diff":
      return <UnidiffRust data={data} />;
    default:
      return <>{data}</>;
  }
};

function CodeBlock({
  st_pk,
  st_sk,
  data,
  fontSize,
  item,
  index,
}: CodeBlockProps) {
  return (
    <Box st_pk={st_pk} st_sk={st_sk}>
      <TextPreview
        data={data}
        fontSize={fontSize}
        item={item}
        fileName={"code.txt"}
        index={index}
        mimetype="text/plain"
        propPrefix="codeblock"
      >
        {render}
      </TextPreview>
    </Box>
  );
}

type CodeBlockOldProps = {
  st_pk: string;
  st_sk: string;
  children: any;
  fontSize: number;
};

function CodeBlockOld({ st_pk, st_sk, children, fontSize }: CodeBlockOldProps) {
  const [hover, setHover] = useState<boolean>(false);
  const [anchor, setAnchor] = useState<HTMLElement>();

  return (
    <Box
      sx={codeStyle(fontSize)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        setAnchor(undefined);
      }}
    >
      {hover && (
        <Box sx={{ position: "absolute", right: 0, zIndex: 5 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              navigator.clipboard.writeText(children);
              setAnchor(e.currentTarget);
              setTimeout(() => {
                setAnchor(undefined);
              }, 1000);
            }}
          >
            <ContentCopyIcon fontSize="inherit" sx={{ color: "white" }} />
          </IconButton>
          <Popper
            open={anchor != null}
            anchorEl={anchor}
            placement="right-start"
            transition
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={350}>
                <Paper>
                  <Typography fontSize={fontSize * 0.8} sx={{ p: 1 }}>
                    copied!
                  </Typography>
                </Paper>
              </Fade>
            )}
          </Popper>
        </Box>
      )}

      <code st_pk={st_pk} st_sk={st_sk}>
        <pre
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: fontSize * 0.85,
          }}
        >
          <CodeBlockText text={children} />
        </pre>
      </code>
    </Box>
  );
}

function unescapeSlackEntities(str: string) {
  return str
    .replaceAll("&amp;", "&")
    .replaceAll("&lt;", "<")
    .replaceAll("&gt;", ">");
}

function injectMarkDownIndecies(
  piece: MdPiece,
  start: [number, number, number]
): [number, number, number] {
  let index = start;
  if (piece.t === "cb" || piece.t === "cbx") {
    piece.codeBlockIndex = index[0];
    index[0] += 1;
  } else if (piece.t === "txt") {
    piece.txtBlockIndex = index[1];
    index[1] += 1;
  } else if (piece.t === "img" || piece.t === "fx") {
    piece.fileIndex = index[2];
    index[2] += 1;
  } else if (piece.t !== "t" && typeof piece.v === "object") {
    for (let subpiece of piece.v as MdPiece[]) {
      index = injectMarkDownIndecies(subpiece, index);
    }
  }

  return index;
}

function compactMarkdown(
  parsed: MdPiece[],
  unescape?: boolean,
  result?: MdPiece[],
  markdownIndeciesStart?: [number, number, number]
) {
  let compacted: MdPiece[] = result || [];
  let markdownIndecies = markdownIndeciesStart || [0, 0, 0];

  const pushRaw = (v: MdPiece) => {
    compacted.push(v);
  };

  const pushUnescaped = (v: MdPiece) => {
    if (typeof v.v === "string") {
      v.v = unescapeSlackEntities(v.v);
    }
    pushRaw(v);
  };

  const push = unescape ? pushUnescaped : pushRaw;

  for (let i = 0; i < parsed.length; ++i) {
    const cur = parsed[i];
    if (cur.t == "t") {
      // tables are weird, do nothing
      push(cur);
    } else if (typeof cur.v === "object") {
      let nested: MdPiece[] = [];
      compacted.push({ t: cur.t, v: nested });
      compactMarkdown(cur.v as MdPiece[], unescape, nested, markdownIndecies);
    } else if (i === 0) {
      push(parsed[i]);
      markdownIndecies = injectMarkDownIndecies(cur, markdownIndecies);
    } else {
      const prev = compacted[compacted.length - 1];
      if (prev.t === "br" && cur.t === "br") {
        compacted[compacted.length - 1] = { t: "br2" };
      } else {
        markdownIndecies = injectMarkDownIndecies(cur, markdownIndecies);
        push(cur);
      }
    }
  }

  return compacted;
}

export function preparseTextWithMath(
  text: string,
  math: { start: number; end: number }[]
): MdPiece[] {
  let flatten: number[] = [];
  for (const m of math) {
    flatten.push(m.start);
    flatten.push(m.end);
  }
  let parsed = JSON.parse(riki.parse_to_json(text, flatten));
  return compactMarkdown(parsed, true);
}

export async function parseMarkdown(
  text: string,
  item: MessageItem
): Promise<MdPiece[]> {
  // const test = [{t: 'h1', v : [{t: 'txt', v: 'hell world'}]}, {t: 'br'}, {t: 'br'}, {t: 'txt', v: 'hello world'}];
  // return compactMarkdown(test);

  // const test = [
  //     { t: "txt", v: "hell world" },
  //     { t: "hr" },
  //     { t: "txt", v: "hello world" },
  //   ];
  //   return compactMarkdown(test);

  // const test = [{t: "txt", v: "value"}, {t: "cx", v: "world", s: "~"}, {t: "txt", v: "world", s: "~"}];
  // return compactMarkdown(test);

  // const test = [{t: "txt", v: "AAA"}, {t: 'sub', v : [{t: 'txt', v: 'hell world'}, {t: "cx", v: "codeline"}]}, {t: 'br'}, {t: 'br'}, {t: 'txt', v: 'hello world'}];
  // return compactMarkdown(test);

  // const test = [{t: "u", v: "@ggerganov"}, {t: "br"}, {t: "u", v: "@acbits"}];
  // return compactMarkdown(test);

  // const test = [{t: "txt", v: "AAA"}, {t: 'ins', v : [{t: 'txt', v: 'hell world'}, {t: "cx", v: "codeline"}]}, {t: 'br'}, {t: 'br'}, {t: 'txt', v: 'hello world'}];
  // return compactMarkdown(test);

  // const test = [{t: "txt", v: "AAA"}, {t: 'mark', v : [{t: 'txt', v: 'hell world'}, {t: "cx", v: "codeline"}]}, {t: 'br'}, {t: 'br'}, {t: 'txt', v: 'hello world'}];
  // return compactMarkdown(test);

  // const test = [
  //   { t: "txt", v: "AAA" },
  //   {
  //     t: "code",
  //     v: [
  //       { t: "txt", v: "hell world" },
  //       { t: "br" },
  //       { t: "br" },
  //       { t: "txt", v: "hello world", s: "~" },
  //       { t: "br" },
  //       { t: "txt", v: "hello world" },
  //     ],
  //   },
  // ];
  // return compactMarkdown(test);

  // const test = JSON.parse('[{"t":"table","v":[{"t":"tr","v":[{"t":"td","v":[{"t":"txt","v":"hello"}]},{"t":"td","v":[{"t":"txt","v":"world"}]}]}]}]');
  // return test;

  // const test = [{t:"a:https://foobar.com",v:[{t: "cx", v: "Google"}, {t: "br"}, {t: "txt", v: "Search"}]}];
  // return test;

  // const test = [
  //   {
  //     t: "h1",
  //     v: [{ t: "txt", v: "Here's your guide to Antler's application process" }],
  //   },
  //   { t: "br" },
  //   { t: "txt", v: "Here's your guide to Antler's application process" },
  //   { t: "br" },
  //   {
  //     t: "txt",
  //     v: "Find everything you need to know about building your company with us",
  //   },
  //   { t: "br" },
  //   {
  //     t: "lx",
  //     v: "https://content.antler.co/e3t/Ctc/2P+113/djW2rd04/MX0VC15WfXfW4DWHd_7R4b8CW6wcfC_5H-FRRN8JCsF25nR3bW7Y9pgv6lZ3n2VD8KqV3-Bb1RW7YP2XD6H1Qh8W3l1jKk89Nkk9W3DcgrK89KnxYW1ZbFhC48byfhW2zJSlw1lPbNFW3SC0p_5zDCGXW70-z198y3wDpN8HWlNqYS25LW2MXjM22PxKK9W36WWyP3DLSjdW5_jg7N2D1KbhW5j-t_p8kWKRBW8RPrL42vRwzKW1nVcpS4LFH42W33768r15lDZyW7M7CtC1M_Q00W4qV-G08vw1t5N35Y50xqVWb0W3mXTpy7gn-ppN2dG8Fmm6PxFVjHTT040FfzsW8yWY-D67f_TFW19bxtr5Gjsz9N4m3NGgRLLNVW83fvSh1CDC0HW1lskq95VbNmBV_QwzH8Vc2WDW3l0thp5hjG3gW8PS14P6wpHmtW2MCRFL4h5BK7W8x-Trl99Tv43W33d5b21KjgCQW3nDkcB2cZnn4W6flGfb3PpF1cW5tCMMX1PLV2pW9gMLH-65XBxkW2_spYm6Tl-v4W7BXFS39kqSmZW8ytjyX7VKr-8W95w82P4yd-sCW2_R_2C3-KVn5f84YxDP04|View in browser",
  //   },
  //   { t: "br" },
  //   {
  //     t: "a:https://content.antler.co/e3t/Ctc/2P+113/djW2rd04/MX0VC15WfXfW4DWHd_7R4b8CW6wcfC_5H-FRRN8JCsDM3qgz0W7lCdLW6lZ3nbW3y9Cl97z0W9GN5zTsL83zG9RN6V8WhsJw1WVVYf2Zv2qXs6RW6wvwWV3M1ndJW7Gd-X63whzkMW5W16Ck74345_W5KRQ2l4Xv7xrW4GHyhV8r2PPtW1lcjGj2HNCmHW4VlN-C2_z-SzN1C2_dll-3nqW2XQKNn5ztgBJN2Sl1LlFYSB2W6g_s0b4gB1xWW7ygq7V7gcQBhW8tZRRB2ndTCfW52nChl4ygYX-W6CHt_X3bmMj8W3P4N2b6_QdndW1h5cPz4hcKXNV1K_ZC373rK7W8ms7t-11LCRkVhWmHr3WDHqHf8XQQsd04",
  //     v: [],
  //   },
  //   { t: "br" },
  //   { t: "txt", v: "Hi Kate," },
  //   { t: "br" },
  //   {
  //     t: "txt",
  //     v: "Thanks for your interest in Antler! Your journey to building something extraordinary starts here.",
  //   },
  //   { t: "br" },
  //   { s: "*", t: "txt", v: "Get ready to dive in" },
  //   {
  //     t: "txt",
  //     v: "\u2014inside this info pack, you\u2019ll find everything you need to know about our application process, the residency, and how Antler can support you at every stage of your startup journey.",
  //   },
  //   { t: "br" },
  //   { s: "*", t: "txt", v: "What\u2019s Inside:" },
  //   { t: "br" },
  //   { t: "txt", v: "* An overview of our " },
  //   { s: "*", t: "txt", v: "residency program" },
  //   { t: "txt", v: " and what you\u2019ll gain" },
  //   { t: "br" },
  //   { t: "txt", v: "* " },
  //   { s: "*", t: "txt", v: "Success stories" },
  //   { t: "txt", v: " from founders in our Swedish & Nordics portfolio" },
  //   { t: "br" },
  //   { t: "txt", v: "* Insight into our " },
  //   { s: "*", t: "txt", v: "global network, mentorship, and funding" },
  //   { t: "br" },
  //   {
  //     t: "lx",
  //     v: "https://content.antler.co/e3t/Ctc/2P+113/djW2rd04/MX0VC15WfXfW4DWHd_7R4b8CW6wcfC_5H-FRRN8JCsFF3qgz0W95jsWP6lZ3lxN8VdMX9yxj-cW3-wChj4yX3ygW4wzRrM2jRb2PW4kf94s6qFN_6W3D8qv95C6fz_N2RVbqxMhl0CW7m_Hlb8H2L-9W1C5K5p819qdFN7c4JKCWfXn8W5pRHDZ2GS0h1W6cqTXF6B7b4SW1x_-L-8VzG5fW5QDqvk191sMCN6M8fFsM3RQdW5BNtTp5YlTHpW3pjcV-4b4n_TW4HcLQN3TrM26W3ZwFzv1B9N1-W2JYqCV34jn9lV1yvvD5BVYGfW2wWvWX4HCsFDW5gB9MD7gy7PCW2DrxqG16K5BBW7w0-FD1PQM5XW2hrPS33JrCW9W8rQv3R87hBrGW2-P4147qtj8rW92slWs4GP4ZyW8mG-Dk1yykqqW5fmWg57GRp71f1WBKJl04| Download now ",
  //   },
  //   { t: "br" },
  //   {
  //     t: "txt",
  //     v: "After reviewing, let's discuss your startup ambitions and the problems you're looking to solve.",
  //   },
  //   { t: "br" },
  //   {
  //     t: "txt",
  //     v: "Don\u2019t worry if you don\u2019t have it all figured out yet\u2014that\u2019s why we\u2019re here. Connect with us by ",
  //   },
  //   {
  //     t: "lx",
  //     v: "https://content.antler.co/e3t/Ctc/2P+113/djW2rd04/MX0VC15WfXfW4DWHd_7R4b8CW6wcfC_5H-FRRN8JCsF23qgz0W7Y8-PT6lZ3kCW66Fvyd1xGDFpW3YPn1C6pJly7W7jNBF471Xr1NW6gm9Bx2ySJgjW2B6GcB8s4YNSW8VS72774Dt7wW7SlHnG4PsVJbW1ShPnB8sX7SdN6YQBldmx9d9W7PMNyz4Wf3WLW13phWZ82yQF3W4WFj1S5Lv9nPW2nH9N84cdWPyN5wL_Xl3Q2b5Vdcn9P2rWYhDW5c-gGY6H73bLVbtkD_5VLZ35W4nnf973DqrNhW1fj3xf7CHmdlW4--fLx1sc061W5-ZYZC34tYcnW1zDDK43G-MbfW8nt1l38sbp-2W4X2DZ35Zgy3YVk6r7g8bLZxyW4bX3bc6hmNS2f2HrwR404|applying here",
  //   },
  //   { t: "txt", v: "." },
  //   { t: "br" },
  //   { t: "txt", v: "All the best," },
  //   { t: "br" },
  //   { t: "txt", v: "The Antler Sweden Team" },
  //   { t: "br" },
  //   {
  //     t: "a:https://content.antler.co/e3t/Ctc/2P+113/djW2rd04/MX0VC15WfXfW4DWHd_7R4b8CW6wcfC_5H-FRRN8JCsDM3qgz0W7lCdLW6lZ3pGW8-zkt28yzS4tW2QY4T84PV0JrW51Spp-4Bt38DW90S2vc4Sn53YW503-ws4kn7BJW4T5WGw22C_lkW702_t65l68xmVPlH7d4v7JKyW1SJYKK770pyLW8KTQfb46Sf01W18Zb407sgmgzW3KF3ky58VP6LW61V0pq74ZB3TW6qSXGc3-Gf5rW1V5qqc4k3VtgW3MmQKP1ZrZ9dW3N_hDl1smjmHW1ftdSg75lLLwW20kVp27_lV0WW7kC9X98XJps7W5bFRPg4bjC6rW2QLStj8HRkYtN7j8pp7qyQfrW3p3q407YVf2lf87D7qs04",
  //     v: [],
  //   },
  //   { t: "txt", v: " " },
  //   {
  //     t: "a:https://content.antler.co/e3t/Ctc/2P+113/djW2rd04/MX0VC15WfXfW4DWHd_7R4b8CW6wcfC_5H-FRRN8JCsFl3qgz0W8wLKSR6lZ3mvW7pkhnl6y7PQzN7KbBPgqGLq-W1WmP651SSkTPW4-X6Wl39tC27W3N3x776bmZ8VW47cdmB1ZhzLJW85J6fg83cZJxW3vbJR45dZkrWW8yh5cy3dFmjSW5mGvQs4FXTJ2VG4nmS4tSJV_W61ClT45g-tclW1HYbTF8mHTtdW6ndPYd6XsPv4W8J9SS_76WHsQW4yQ82M238SYcW2CgLYt5KL_CvW7R-D-T7g50R6VfFSBl248FSbW8_MgBB3DHh-CW69FRrK8G4QnLW3Ls6SG88TtSwW4wHv-g6RFb0-W4gdkwR6PrwMJW7KgdXg5PLlHBW2wZLgq63-p7qW6b9bGg8H2Wt0W3fr0vs77VsNZf57136M04",
  //     v: [],
  //   },
  //   { t: "txt", v: " " },
  //   {
  //     t: "a:https://content.antler.co/e3t/Ctc/2P+113/djW2rd04/MX0VC15WfXfW4DWHd_7R4b8CW6wcfC_5H-FRRN8JCsCz5nR3bW50kH_H6lZ3kYW6lKNy21PcGPbW5ctw4_2J6_vhN85hZHfr1b7NW78sXVL1Xgys1W8JG6Dj5XzWFnMRX4pQ_HT6cW4x4Grp6pJ-hlW1gVwXq28rY4WW1hrX4D4gQMr9W7Ts6JN1DgKrGW35K73M42h3LnW5w1Hvh3cwQhKW3rJgFD4FGyDrW98qB4K3M0jXkW1KV2tR7-4c_2W7s_xSx9l2t6HW794ST93ZxTNlW4rrlLG1KT5JkVmzKKr2T_gyWW7qhjNh6-P7qrN1S60JpbDN_4W2sT5gR5lkhmxW7jpDnJ3v3gmQMRLDkLSv03bV34yXT80NcP7W7c73N11MTxkrW3LWVSd1Z_PHvMZPRZcfKKRjW1fyWQ36M-bMMW2VR6Mt8CqNG1W82VRHR2DhKgTW5NMGr76MQbxxf2NBdmq04",
  //     v: [],
  //   },
  //   { t: "txt", v: " " },
  //   {
  //     t: "a:https://content.antler.co/e3t/Ctc/2P+113/djW2rd04/MX0VC15WfXfW4DWHd_7R4b8CW6wcfC_5H-FRRN8JCsF23qgz0W7Y8-PT6lZ3m8W2gdl1g1-5DgwMwTlx3mzHSYN9d4GZmK6QrGV8Kv7t4zpJ4gW1PK_gz2-MnvBW2LlBkB4FhwZxN2_YcCvJS6pCW54xc-c7r59trW7s7FZ924MrlkW90Z5wl6b83zxW8vpgP382_WFBW41jGP_1cb_cKVBg7fT3D85KqW6-1pdV2Q-XQcN765HDMfHWgVW6nCKx26nPCgnW5qCspx3P5gW8VR1y6892p1gDN7pQ1rfW_vPHVVNpYr2LRCJ4W7c2cxL1lX10dVlvh1b8cz5pFVsScLn5kYjc9VZLf484spb0nN3wRKgdsc6Y5W4vFr2h7vbSBkf5QgmdR04",
  //     v: [],
  //   },
  //   { t: "txt", v: " " },
  //   {
  //     t: "a:https://content.antler.co/e3t/Ctc/2P+113/djW2rd04/MX0VC15WfXfW4DWHd_7R4b8CW6wcfC_5H-FRRN8JCsF23qgz0W7Y8-PT6lZ3lDN4n8HHRfKs0XVS1TVS77qr2BW5g9L-X7StDY2W1Sx-wJ6b1SNRW840Tvt319lLZW9392Cq2_7-4SW4_WqcF5PGJndW13p1Np7MycKxW4L2cPm8Jky1cW2FxK6P7vhRq_W6gl2n04ZgQGnW4-hctb7z4qVSW7fm6d84-vLC4W6CxR-K3LxdhNW71WRCn8fNTS5Vq-Ql55Kb-tbW640b-g2YlDKZW8h0-vp5zPNxtW7Cy_l41H04rVW2Fn_sd25C_lKW5RbCFt96WwLtW1QJHCt5vm8gsW8kpNHN1yzmj_W3g60Cj7B7b0hW1jZFLb6t7Fq4W1X8ymC94RwbCf6DGbcT04",
  //     v: [],
  //   },
  //   { t: "txt", v: " " },
  //   {
  //     t: "a:https://content.antler.co/e3t/Ctc/2P+113/djW2rd04/MX0VC15WfXfW4DWHd_7R4b8CW6wcfC_5H-FRRN8JCsF23qgz0W7Y8-PT6lZ3kSW4PFP7j1SWcj8W6m5Rq454QSddW50mQTy4_GH7_W23xsHW214Q0XMVyZCy6QG6rW7SPhMl7Zt7LNW1vxCrg1YhLqzW5GXfK07zJTxbW2XgM8x6bFSrNN67-rsQf_wksW2F9RWT7R6m7TW3pJBZp75vY0_W7f74D02SHjHQW1gp_N53_gYM1W2jT5T75jVwpwW53m_bl3_vdL3W5MQQSl2mngQbN8GSxJQ21VRnN6R-lBpb9G62W4bfY_p8lfLjWW7mdh-s7-9b1xV-QK-N8P_GcQW2tHR-H4MqZfWW1LjsRt4QyRY6W2vf4Rg24tdNtW4QVgxh3395S2f585K6d04",
  //     v: [],
  //   },
  //   { t: "br" },
  //   {
  //     t: "txt",
  //     v: "\u00a9 2026 Antler Innovation Pty Ltd. All right reserved.",
  //   },
  //   { t: "br" },
  //   { t: "br" },
  //   { t: "txt", v: "Antler Global HQ, 128 Prinsep St, Singapore." },
  //   { t: "br" },
  //   { t: "br" },
  //   { t: "txt", v: "Want to change how you receive these emails? You can" },
  //   { t: "br" },
  //   {
  //     t: "lx",
  //     v: "https://content.antler.co/hs/preferences-center/en/page?data=W2nVjwf3Y2x8XW3z0pQP3SH_--W41BngR2qSZsDW3byWxW2HT6wzW2KB01q2r637cW3jpRx-45SXKGW2WrfLj2PB0VFW2xJl1y3bbmydW25k3pV2PtBtwW3C9Sql2nH5xNW47WFSf2CDDp1W3SHDn93g6G50W2CKFHz32bpCgW3dgkjC3SMlMcW1Qg_hp3XT-RtW3jqg8B2s-pr2W1Bs6XW36B7VPW3XxfjZ3GYVPdW1WYpgy4ft-MgW4mdrYw1S8ZMQW232dcf23b9x9W2qG1Mn2KNl_6W2w3ZSm45MwX6W32cc901XbdnXW30HQgH2xHw3GW2Ymm1L45vNVrW21sBQk1QBFKJW3R0FWQ3H9kFQW309hv72WQH6JW3d36lF3XQ8s9W2RLtRC47Qft5W3NPn3X3SJpfgW4hy2zS2WhRrsW3VWtKs2MwqDlW3yXPS92sKcKsW3F77C03VxVL6W4fslfD22WpYkW2vzxmj1Qnpy1W3ZFjvZ23lXzDW2TQ49M3g7VzrW1V99GM3K5sVgW47DDjp1LHcT_W3GJFj43M8bh5W4ksjLh1VtS15W34xvb234BT0MW34plTy32JTNnW2Yxqhq38BsFwW2zNQ-g3db_YHW4tGbgn34nXZSW1_62LC3bzRBbW3bjbKJ3XLWdMW212VJV2t5WC1W3QLG0F45ppt0W2xQf413QQrckW1LF5n42PnWFQf3XFts904&utm_campaign=40507615-Sweden%20Newsletter%20Stand%20Alone&utm_source=hs_automation&utm_medium=email&utm_content=102973676&_hsenc=p2ANqtz-9JJYZwxAXLNLpzyLg79ouDgkxuJB79Mk2uW90B2rCwK94bonqtygme_E-PQC5chmp0LO_PbAXsAmpqhngbboEyfrnYcQ&_hsmi=102973676|manage your preferences",
  //   },
  //   { t: "txt", v: " or " },
  //   {
  //     t: "lx",
  //     v: "https://content.antler.co/hs/preferences-center/en/direct?data=W2nVjwf3Y2x8XW3z0pQP3SH_--W41BngR2qSZsDW3byWxW2HT6wzW2KB01q2r637cW3jpRx-45SXKGW2WrfLj2PB0VFW2xJl1y3bbmydW25k3pV2PtBtwW3C9Sql2nH5xNW47WFSf2CDDp1W3SHDn93g6G50W2CKFHz32bpCgW3dgkjC3SMlMcW1Qg_hp3XT-RtW3jqg8B2s-pr2W1Bs6XW36B7VPW3XxfjZ3GYVPdW1WYpgy4ft-MgW4mdrYw1S8ZMQW232dcf23b9x9W2qG1Mn2KNl_6W2w3ZSm45MwX6W32cc901XbdnXW30HQgH2xHw3GW2Ymm1L45vNVrW21sBQk1QBFKJW3R0FWQ3H9kFQW309hv72WQH6JW3d36lF3XQ8s9W2RLtRC47Qft5W3NPn3X3SJpfgW4hy2zS2WhRrsW3VWtKs2MwqDlW3yXPS92sKcKsW3F77C03VxVL6W4fslfD22WpYkW2vzxmj1Qnpy1W3ZFjvZ23lXzDW2TQ49M3g7VzrW1V99GM3K5sVgW47DDjp1LHcT_W3GJFj43M8bh5W4ksjLh1VtS15W34xvb234BT0MW34plTy32JTNnW2Yxqhq38BsFwW2zNQ-g3db_YHW4tGbgn34nXZSW1_62LC3bzRBbW3bjbKJ3XLWdMW212VJV2t5WC1W3QLG0F45ppt0W2xQf413QQrckW1LF5n42PnWFQf3XFts904&utm_campaign=40507615-Sweden%20Newsletter%20Stand%20Alone&utm_source=hs_automation&utm_medium=email&utm_content=102973676&_hsenc=p2ANqtz-9JJYZwxAXLNLpzyLg79ouDgkxuJB79Mk2uW90B2rCwK94bonqtygme_E-PQC5chmp0LO_PbAXsAmpqhngbboEyfrnYcQ&_hsmi=102973676|unsubscribe",
  //   },
  //   { t: "txt", v: "." },
  // ];

  // return test;

  if ("st_pp" in item) {
    if (typeof item.st_pp === "string") {
      return compactMarkdown(await u.parseJsonFromBinary(item.st_pp));
    } else if (item.st_pp) {
      return compactMarkdown(item.st_pp);
    }
  }
  return preparseTextWithMath(text, []);
}

const getStableKey = (chunk: MdPiece) => {
  if (chunk.t === "cbx" || chunk.t === "cb") {
    return `codeblock-${chunk.codeBlockIndex}`;
  } else if (chunk.t === "img" || chunk.t === "fx") {
    return `file-${chunk.fileIndex}`;
  }
  return undefined;
};

type MarkdownProps = {
  parsed: MdPiece[];
  item: MessageItem;
  fontSize?: number;
  start: number;
  includeNodeIds?: boolean;
  sx?: any;
};
export function Markdown({
  parsed,
  item,
  fontSize,
  start,
  sx,
  includeNodeIds,
}: MarkdownProps) {
  // useEffect(()=> {
  //   if (!Array.isArray(parsed)) {
  //     console.log("Invalid parsed markdown:", item);
  //   }
  //   console.log(item.PK);
  //   console.log(item.SK);
  //   console.log(parsed);
  // }, [parsed]);

  return (
    <>
      {parsed.map((chunk, i) => (
        <MarkdownChunk
          chunk={chunk}
          type={chunk.t}
          value={chunk.v}
          refs={chunk.r}
          textStyle={chunk.s}
          customStyle={chunk.css}
          item={item}
          fontSize={fontSize || DEFAULT_FONT_SIZE}
          index={start + i}
          end={start + parsed.length}
          sx={sx}
          includeNodeIds={includeNodeIds}
          key={getStableKey(chunk) || start + i}
        />
      ))}
    </>
  );
}

export function QuoteBar({ children }: { children: any }) {
  return (
    <Box
      sx={{
        position: "relative",
        pl: 2,
        py: 1,
        ml: 0.5,
        my: 0.5,
        borderRadius: "0 8px 8px 0",
        backgroundColor: "var(--quote-bg, rgba(128, 128, 128, 0.08))",
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "4px",
          backgroundColor: "var(--quote-border, dimgrey)",
          borderRadius: "4px 0 0 4px",
        },
      }}
    >
      {children}
    </Box>
  );
}

type MarkdownChunkProps = {
  chunk: MdPiece;
  type: string;
  value?: string | MdPiece[] | MdPiece[][][];
  item: MessageItem;
  refs?: string[];
  textStyle?: string;
  fontSize: number;
  index: number;
  end: number;
  sx?: any;
  customStyle?: { [key: string]: any };
  includeNodeIds?: boolean;
};

function MarkdownChunk({
  chunk,
  type,
  value,
  item,
  refs,
  textStyle,
  fontSize,
  index,
  end,
  sx,
  customStyle,
  includeNodeIds,
}: MarkdownChunkProps) {
  const projectName = useAtomValue(projectPickerState);
  const refStory = { st_pk: item.PK, st_sk: item.SK };
  const setSearch = useSetAtom(searchState);

  const getCustomStyle = () => {
    if (!customStyle) {
      return {};
    }

    let out: any = {};
    for (const [k, v] of Object.entries(customStyle)) {
      if (k === "font-weight") {
        out.fontWeight = v;
      } else if (k === "font-size") {
        out.fontSize = v;
      } else if (k == "color") {
        out[k] = v;
      }
    }
    return out;
  };

  const urlFromStoryKey = (key: string) => {
    return (
      window.location.origin +
      `/story/${projectName}/${key.split("-")[1]}${window.location.search}`
    );
  };

  const urlFromRef = (ref: string) => {
    const key = ref.slice(1, -1);
    let url =
      window.location.origin +
      `/story/${projectName}/${encodeURIComponent(key)}${
        window.location.search
      }`;

    const timeref = item.st_created_at || item.ts || item.event_ts;
    if (timeref) {
      url += `/${encodeURIComponent(timeref)}`;
    }

    return url;
  };

  const urlFromLink = (link: string, trim?: number) => {
    const trimmed = trim ? link.slice(trim, -trim) : link;
    return trimmed.split("|")[0];
  };

  const annotationFromLink = (link: string, trim?: number) => {
    const v = trim ? link.slice(trim, -trim) : link;
    const delim = v.indexOf("|");
    if (delim === -1) {
      return v;
    } else {
      const annotation = v.slice(delim + 1);
      return annotation.length ? annotation : v.slice(0, delim);
    }
  };

  const fileNameFromLinkWithAnnotation = (link: string) => {
    const v = link;
    const delim = v.indexOf("|");
    if (delim === -1) {
      const url = new URL(v);
      const segments = url.pathname.split("/").filter(Boolean);
      const lastSegment = segments.at(-1);
      return lastSegment;
    } else {
      const annotation = v.slice(delim + 1);
      return annotation.length ? annotation : v.slice(0, delim);
    }
  };

  const getCode = (value: string | MdPiece[], wrap: number) => {
    if (typeof value === "string") {
      return value.slice(wrap, -wrap);
    } else {
      let res = "";
      for (let p of value) {
        if (p.t === "txt") {
          res += p.v;
        } else if (p.t === "lnk") {
          res += p.v!.slice(1, -1);
        } else if (p.t == "br") {
          res += "\n";
        }
      }
      return res;
    }
  };

  const convertTextStyle = (textStyle?: string) => {
    let sx: any = {};
    if (textStyle) {
      let done = false;
      if (textStyle.includes("_")) {
        sx.fontStyle = "italic";
        done = true;
      }
      if (textStyle.includes("*")) {
        sx.fontWeight = "bold";
        done = true;
      }
      if (textStyle.includes("~")) {
        sx.textDecoration = "line-through";
        done = true;
      }
      if (textStyle.includes("!")) {
        sx.backgroundColor = "var(--md-highlight-bg)";
        sx.color = "var(--md-highlight-text)";
        done = true;
      }

      if (!done) {
        // previous format
        if (textStyle.includes("i")) {
          sx.fontStyle = "italic";
        }
        if (textStyle.includes("b")) {
          sx.fontWeight = "bold";
        }
        if (textStyle.includes("s")) {
          sx.textDecoration = "line-through";
        }
      }
    }

    return sx;
  };

  if (type === "txt") {
    // text
    return (
      <Typography
        {...refStory}
        component="span"
        fontSize={fontSize}
        style={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          ...getCustomStyle(),
        }}
        st_md={
          true || includeNodeIds
            ? `${chunk.txtBlockIndex}:${chunk.txtBlockOffset || 0}`
            : undefined
        }
        sx={convertTextStyle(textStyle)}
      >
        {value as string}
      </Typography>
    );
  } else if (type === "stk") {
    // strikethrough -- deprecated
    return (
      <Typography
        component="span"
        fontSize={fontSize}
        sx={{ textDecoration: "line-through" }}
      >
        {(value as string).slice(1, -1)}
      </Typography>
    );
  } else if (type === "it") {
    // italic -- deprecated
    return (
      <Typography
        component="span"
        fontSize={fontSize}
        sx={{ fontStyle: "italic" }}
      >
        {(value as string).slice(1, -1)}
      </Typography>
    );
  } else if (type === "bld") {
    // bold -- deprecated
    return (
      <Typography
        component="span"
        fontSize={fontSize}
        sx={{ fontWeight: "bold" }}
      >
        {(value as string).slice(1, -1)}
      </Typography>
    );
  } else if (type === "url") {
    // raw url
    return (
      <>
        <Link href={value as string} fontSize={fontSize} sx={sx}>
          {value as string}
        </Link>
        {refs && refs.length && (
          <sup style={{ fontSize: fontSize * 0.62 }}>
            <StoryReference
              url={urlFromRef(`[${refs[0]}]`)}
              storyId={`[${refs[0]}]`}
              sx={sx}
            />
          </sup>
        )}
      </>
    );
  } else if (type === "br") {
    // line break
    return <br />;
  } else if (type === "hr") {
    return (
      <>
        <div
          style={{
            height: "3px",
            width: "50%",
            backgroundColor: "#eeeeee",
            marginTop: 5,
            marginBottom: 5,
          }}
        />
      </>
    );
  } else if (type === "br2") {
    // a pair of line breaks
    return (
      <>
        <br />
        <span style={{ display: "block", height: "0.3em", width: "0px" }} />
      </>
    );
  } else if (type === "rk") {
    // reference key like STORY-123
    return (
      <StoryReference
        storyId={value as string}
        url={urlFromStoryKey(value as string)}
        fontSize={fontSize}
        sx={sx}
      />
    );
  } else if (type === "ref") {
    // reference enclosed in square bracket like [123]
    return (
      <StoryReference
        storyId={value as string}
        url={urlFromRef(value as string)}
        fontSize={fontSize}
        sx={sx}
      />
    );
  } else if (type === "lnk") {
    // link enclosed in angle brackets <...>
    return (
      <>
        <Link
          href={urlFromLink(value as string, 1)}
          sx={{
            ...convertTextStyle(textStyle),
            fontSize: fontSize,
            ...(sx || {}),
          }}
        >
          {annotationFromLink(value as string, 1)}
        </Link>
        {refs && refs.length && (
          <sup style={{ fontSize: fontSize * 0.62 }}>
            <StoryReference
              url={urlFromRef(`[${refs[0]}]`)}
              storyId={`[${refs[0]}]`}
              sx={sx}
            />
          </sup>
        )}
      </>
    );
  } else if (type === "fr") {
    // github's footnote reference
    return (
      <sup style={{ fontSize: fontSize * 0.62 }}>
        <Link href={`#${item.PK}-${item.SK}-footnote-${value}`} sx={sx}>
          [{value as string}]
        </Link>
      </sup>
    );
  } else if (type === "fd") {
    // github's footnote definition
    return (
      <>
        {value === "1" && <Divider />}
        <span id={`${item.PK}-${item.SK}-footnote-${value}`}>
          {value + ". "}
        </span>
      </>
    );
  } else if (type === "footnote") {
    // github's footnote
    return (
      <>
        <br />
        <br />
        <Markdown
          parsed={value as any[]}
          item={item}
          fontSize={fontSize}
          start={end}
          sx={sx}
        />
      </>
    );
  } else if (type === "lx") {
    // github's link
    return (
      <>
        <Link
          href={urlFromLink(value as string, 0)}
          sx={{ ...convertTextStyle(textStyle), ...(sx || {}) }}
          fontSize={fontSize}
          style={getCustomStyle()}
        >
          {annotationFromLink(value as string, 0)}
        </Link>
        {refs && refs.length && (
          <sup style={{ fontSize: fontSize * 0.62 }}>
            <StoryReference
              url={urlFromRef(`[${refs[0]}]`)}
              storyId={`[${refs[0]}]`}
              sx={sx}
            />
          </sup>
        )}
      </>
    );
  } else if (type === "fx") {
    // github's file attachment
    return (
      <FileUploadPreview
        files={[
          {
            name: fileNameFromLinkWithAnnotation(value as string),
            uri: urlFromLink(value as string, 0),
          },
        ]}
        item={item}
        markdown
      />
    );
  } else if (type === "u") {
    // slack's username: <@U04QPK9JM28>
    return (
      <Typography
        component="span"
        fontSize={fontSize}
        style={{ whiteSpace: "pre-wrap" }}
        sx={{
          backgroundColor: "var(--md-username-bg)",
          borderRadius: "4px",
          p: 0.3,
          pl: 0.5,
          pr: 0.5,
          fontWeight: 500,
        }}
      >
        <UserName
          user={{ app: item.st_source, userId: value as string }}
          prefixName="@"
          onClick={(accounts: AccountInfo[]) => {
            setSearch([accounts]);
          }}
        />
      </Typography>
    );
  } else if (type === "c") {
    // slacks' channel name
    return (
      <Typography
        component="span"
        fontSize={fontSize}
        style={{ whiteSpace: "pre-wrap" }}
      >
        #<ChannelName channelId={(value as string).slice(2, -1)} />
      </Typography>
    );
  } else if (type === "e") {
    // emoji like :smile:
    return (
      <Typography
        component="span"
        fontSize={translateEmoji(value as string) ? fontSize * 1.25 : fontSize}
        style={{ whiteSpace: "pre-wrap", lineHeight: "0em" }}
      >
        {translateEmoji(value as string) || (value as string)}
      </Typography>
    );
  } else if (type === "b") {
    // slack's broadcast like @here, @channel, @everyone
    return (
      <SlackMention
        type="broadcast"
        value={value as string}
        fontSize={fontSize}
      />
    );
  } else if (type === "s") {
    // slack's subteam/usergroup mention
    return (
      <SlackMention
        type="subteam"
        value={value as string}
        fontSize={fontSize}
      />
    );
  } else if (type === "cl") {
    // codeline `code`
    return (
      <CodeLine {...refStory} fontSize={fontSize}>
        {getCode(value as string | MdPiece[], 1)}
      </CodeLine>
    );
  } else if (type === "cx") {
    // github's codeline
    return (
      <CodeLine
        {...refStory}
        fontSize={fontSize}
        sx={convertTextStyle(textStyle)}
      >
        {value}
      </CodeLine>
    );
  } else if (type === "cb") {
    // codeblock ```code block```
    // or unterminated code block ```code block
    let code = "";
    if (typeof value === "string") {
      code = value.slice(3);
      if (code.endsWith("```")) {
        code = code.slice(0, code.length - 3);
      }
    } else {
      code = getCode(value as string | MdPiece[], 3);
    }
    return (
      <CodeBlock
        {...refStory}
        fontSize={fontSize}
        item={item}
        index={chunk.codeBlockIndex || 0}
        data={u.trimNewLine(code)}
      />
    );
  } else if (type === "cbx") {
    //github's code block
    return (
      <CodeBlock
        {...refStory}
        fontSize={fontSize}
        item={item}
        index={chunk.codeBlockIndex || 0}
        data={u.trimNewLine(value as string)}
      />
    );
  } else if (type === "q") {
    // quote
    return (
      <QuoteBar>
        <Markdown
          parsed={value as any[]}
          item={item}
          fontSize={fontSize}
          start={end}
          sx={sx}
        />
      </QuoteBar>
    );
  } else if (type === "t") {
    // table
    return (
      <TableContainer>
        <Table size="small" sx={{ width: "auto" }}>
          <TableHead>
            <TableRow sx={{ borderBottom: "1px solid #e0e0e0" }}>
              {(value as any[])[0].map((cell: any, i: number) => (
                <TableCell key={i}>
                  <Markdown
                    parsed={cell}
                    item={item}
                    fontSize={fontSize}
                    start={end}
                    sx={sx}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(value as any[]).slice(1).map((row, i) => (
              <TableRow key={i} sx={{ borderBottom: "1px solid #e0e0e0" }}>
                {row.map((cell: any, j: number) => (
                  <TableCell key={j}>
                    <Markdown
                      parsed={cell}
                      item={item}
                      fontSize={fontSize}
                      start={end}
                      sx={sx}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  } else if (type === "h") {
    return (
      <>
        <Typography
          component="span"
          fontSize={fontSize}
          style={{ whiteSpace: "pre-wrap" }}
        >
          {value as string}
          {refs && refs.length && (
            <sup style={{ fontSize: fontSize * 0.62 }}>
              <StoryReference
                url={urlFromRef(`[${refs[0]}]`)}
                storyId={`[${refs[0]}]`}
                sx={sx}
              />
            </sup>
          )}
        </Typography>
      </>
    );
  } else if (type === "img") {
    // github image
    return (
      <FileUploadPreview
        files={[
          {
            type: "image/unknown",
            name: fileNameFromLinkWithAnnotation(value as string),
            uri: urlFromLink(value as string, 0),
          },
        ]}
        item={item}
        markdown
      />
    );
  } else if (type[0] === "h" && type[1] >= "1" && type[1] <= "6") {
    // header
    return (
      <Markdown
        parsed={value as any[]}
        item={item}
        fontSize={fontSize * 1.2}
        start={end}
        sx={sx}
      />
    );
  } else if (type === "sup") {
    return (
      <sup>
        <Markdown
          parsed={value as any[]}
          item={item}
          fontSize={fontSize * 0.8}
          start={end}
          sx={sx}
        />
      </sup>
    );
  } else if (type === "sub") {
    return (
      <sub>
        <Markdown parsed={value as any[]} item={item} start={end} sx={sx} />
      </sub>
    );
  } else if (type === "mark") {
    return (
      <mark>
        <Markdown parsed={value as any[]} item={item} start={end} sx={sx} />
      </mark>
    );
  } else if (type === "ins") {
    return (
      <ins>
        <Markdown parsed={value as MdPiece[]} item={item} start={end} sx={sx} />
      </ins>
    );
  } else if (type === "code") {
    return (
      <CodeLine
        {...refStory}
        fontSize={fontSize}
        sx={convertTextStyle(textStyle)}
      >
        <Markdown parsed={value as MdPiece[]} item={item} start={end} sx={sx} />
      </CodeLine>
    );
  } else if (type === "table") {
    // table
    // example {t: "table", v: [t: "tr", v: [{t: "td", v: [{"txt", "hello"}]}]]}
    return (
      <TableContainer>
        <Table size="small" sx={{ width: "auto" }}>
          <TableBody>
            {value &&
              (value as MdPiece[]).map((row, i) => (
                <TableRow key={i} sx={{ borderBottom: "1px solid #e0e0e0" }}>
                  {row.t === "tr" &&
                    row.v &&
                    (row.v as MdPiece[]).map((cell: any, j: number) => (
                      <TableCell key={j}>
                        <Markdown
                          parsed={cell.v}
                          item={item}
                          fontSize={fontSize}
                          start={end}
                          sx={sx}
                        />
                      </TableCell>
                    ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  } else if (type.startsWith("a:")) {
    return (
      <Link href={type.slice(2)} target="_blank" rel="noreferrer">
        <Markdown
          parsed={value as MdPiece[]}
          item={item}
          start={end}
          sx={sx}
          fontSize={fontSize}
        />
      </Link>
    );
  } else {
    return <div />;
  }
}

type StoryReferenceProps = {
  storyId: string;
  url: string;
  fontSize?: number;
  sx: any;
};
function StoryReference({ storyId, url, fontSize, sx }: StoryReferenceProps) {
  return (
    <StoryPopup
      storyMoniker={storyId}
      enterDelay={300}
      previewComponent={DarkStoryPreview}
    >
      <Link href={url} sx={{ fontSize: fontSize, ...(sx || {}) }}>
        {storyId}
      </Link>
    </StoryPopup>
  );
}

const getUserId = (userId: string, sourceApp?: string) => {
  if (
    sourceApp === "slack" ||
    (!sourceApp && userId.length > 0 && userId[0] == "<")
  ) {
    return userId.slice(2, -1);
  }

  if (userId.length > 0 && userId[0] === "@") {
    return userId.slice(1);
  }

  return userId;
};

export function UserName({
  user,
  onClick,
  fontSize,
  prefixName,
  textColor,
}: {
  user: { userId: string; app?: string };
  onClick?: (accounts: AccountInfo[]) => void;
  fontSize?: number;
  prefixName?: string;
  textColor?: string;
}) {
  const data = useAtomValue(peopleIndexState);
  const [displayUser, setDisplayUser] = useState<string>(
    getUserId(user.userId)
  );

  useEffect(() => {
    if (data) {
      setDisplayUser((prev: string) => {
        const userId = getUserId(user.userId);
        const user_key = `${user.app}:${userId}`;
        const found = data.get(user_key);
        if (found) {
          let name = "";
          for (const acc of found) {
            if (acc.name && acc.name.length > name.length) {
              name = acc.name;
            }
          }
          if (name) {
            return name;
          }
          for (const acc of found) {
            if (acc.email) {
              return acc.email;
            }
          }

          for (const acc of found) {
            if (acc.username) {
              return acc.username;
            }
          }
        }

        return userId;
      });
    }
  }, [data, user]);

  const handleClick = () => {
    if (!data || !onClick) {
      return;
    }
    const userId = getUserId(user.userId);
    const key = `${user.app}:${userId}`;
    const found = data.get(key);
    if (!found) {
      return;
    }
    onClick(found);
  };

  if (!onClick) {
    return <>{(prefixName || "") + displayUser}</>;
  } else {
    return (
      <ButtonLink
        text={(prefixName || "") + displayUser}
        onclick={handleClick}
        fontSize={fontSize}
        textColor={textColor}
      ></ButtonLink>
    );
  }
}

function ChannelName({ channelId }: { channelId: string }) {
  const data = useAtomValue(activeLabelsState);
  const [channel, setChannel] = useState<string>(
    channelId.split("|").slice(-1)[0]
  );

  useEffect(() => {
    if (data) {
      setChannel((prev: string) => {
        if (prev && data.prev) {
          const display = data[channel].display;
          let out = data[channel].name;
          if (typeof display === "string" && display) {
            out = display;
          }
          if (out && out.startsWith("#")) {
            return out.slice(1);
          }
          if (out) {
            return out;
          }
        }
        return prev;
      });
    }
  }, [data]);

  return <>{channel}</>;
}
